import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { prepareFileForStorage, canStoreOffline, MAX_OFFLINE_FILE_SIZE, formatFileSize } from '@/utils/offlineFileHandler';

export interface Cumplimiento {
  id: string;
  documento_id: string;
  user_id: string;
  acto_administrativo: string | null;
  articulo: string | null;
  numeral_literal: string | null;
  descripcion: string | null;
  created_at: string;
  updated_at: string;
}

export interface EvidenciaCumplimiento {
  id: string;
  cumplimiento_id: string;
  user_id: string;
  nombre_archivo: string;
  nombre_original: string;
  tipo_archivo: string;
  tamano_archivo: number;
  url_archivo: string;
  ruta_storage: string;
  created_at: string;
}

export const useCumplimiento = (documentoId: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { cacheData, getCachedData, addPendingOperation, isOnline } = useOfflineStorage();
  const [cumplimientos, setCumplimientos] = useState<Cumplimiento[]>([]);
  const [cumplimientoActual, setCumplimientoActual] = useState<Cumplimiento | null>(null);
  const [evidencias, setEvidencias] = useState<EvidenciaCumplimiento[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCumplimientos = async () => {
    if (!user) return;

    const cumplimientosCacheKey = `cumplimientos_${documentoId}_${user.id}`;

    try {
      setLoading(true);

      if (!isOnline) {
        const cachedCumplimientos = await getCachedData('cumplimientos', cumplimientosCacheKey);
        if (cachedCumplimientos) {
          setCumplimientos(cachedCumplimientos);
        }

        if (cumplimientoActual) {
          const evidenciasCacheKey = `evidencias_${cumplimientoActual.id}`;
          const cachedEvidencias = await getCachedData('evidencias', evidenciasCacheKey);
          if (cachedEvidencias) {
            setEvidencias(cachedEvidencias);
          }
        }
        return;
      }
      
      const { data: cumplimientosData, error: cumplimientosError } = await supabase
        .from('cumplimientos_documentos')
        .select('*')
        .eq('documento_id', documentoId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (cumplimientosError) throw cumplimientosError;

      const cumps = cumplimientosData || [];
      setCumplimientos(cumps);
      await cacheData('cumplimientos', cumplimientosCacheKey, cumps);

      if (cumplimientoActual) {
        const { data: evidenciasData, error: evidenciasError } = await supabase
          .from('evidencias_cumplimiento')
          .select('*')
          .eq('cumplimiento_id', cumplimientoActual.id)
          .order('created_at', { ascending: false });

        if (evidenciasError) throw evidenciasError;
        
        const evids = evidenciasData || [];
        setEvidencias(evids);
        await cacheData('evidencias', `evidencias_${cumplimientoActual.id}`, evids);
      } else {
        setEvidencias([]);
      }
    } catch (error) {
      console.error('Error loading cumplimientos:', error);
      
      const cachedCumplimientos = await getCachedData('cumplimientos', cumplimientosCacheKey);
      if (cachedCumplimientos) {
        setCumplimientos(cachedCumplimientos);
        toast({
          title: "Modo offline",
          description: "Mostrando datos guardados localmente",
        });
      } else {
        toast({
          title: "Error",
          description: "No se pudo cargar la información de cumplimiento",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEvidenciasForCumplimiento = async (cumplimientoId: string) => {
    try {
      const { data: evidenciasData, error: evidenciasError } = await supabase
        .from('evidencias_cumplimiento')
        .select('*')
        .eq('cumplimiento_id', cumplimientoId)
        .order('created_at', { ascending: false });

      if (evidenciasError) throw evidenciasError;
      setEvidencias(evidenciasData || []);
    } catch (error) {
      console.error('Error loading evidencias:', error);
      setEvidencias([]);
    }
  };

  const selectCumplimiento = async (cumplimiento: Cumplimiento | null) => {
    setCumplimientoActual(cumplimiento);
    if (cumplimiento) {
      await loadEvidenciasForCumplimiento(cumplimiento.id);
    } else {
      setEvidencias([]);
    }
  };

  const createNewCumplimiento = () => {
    setCumplimientoActual(null);
    setEvidencias([]);
  };

  const saveCumplimiento = async (data: {
    acto_administrativo: string;
    articulo: string;
    numeral_literal: string;
    descripcion: string;
  }) => {
    if (!user) throw new Error('No user authenticated');

    try {
      if (!isOnline) {
        const tempId = cumplimientoActual?.id || `temp_${Date.now()}`;
        const cumplimientoData = {
          id: tempId,
          documento_id: documentoId,
          user_id: user.id,
          ...data,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        await addPendingOperation(
          cumplimientoActual ? 'update' : 'create',
          'cumplimientos_documentos',
          cumplimientoData
        );

        if (cumplimientoActual) {
          setCumplimientoActual({ ...cumplimientoActual, ...data });
        } else {
          setCumplimientoActual(cumplimientoData as Cumplimiento);
        }

        toast({
          title: "Guardado offline",
          description: "Se sincronizará cuando te conectes a internet",
        });

        return;
      }

      if (cumplimientoActual) {
        const { data: updatedData, error } = await supabase
          .from('cumplimientos_documentos')
          .update(data)
          .eq('id', cumplimientoActual.id)
          .select()
          .single();

        if (error) throw error;
        setCumplimientoActual(updatedData);
      } else {
        const { data: newData, error } = await supabase
          .from('cumplimientos_documentos')
          .insert({
            documento_id: documentoId,
            user_id: user.id,
            ...data
          })
          .select()
          .single();

        if (error) throw error;
        setCumplimientoActual(newData);
      }
      
      await loadCumplimientos();
    } catch (error) {
      console.error('Error saving cumplimiento:', error);
      throw error;
    }
  };

  const deleteCumplimiento = async (cumplimientoId: string) => {
    try {
      const { error } = await supabase
        .from('cumplimientos_documentos')
        .delete()
        .eq('id', cumplimientoId);

      if (error) throw error;

      // If we deleted the current cumplimiento, clear it
      if (cumplimientoActual?.id === cumplimientoId) {
        setCumplimientoActual(null);
        setEvidencias([]);
      }

      // Reload cumplimientos
      await loadCumplimientos();
    } catch (error) {
      console.error('Error deleting cumplimiento:', error);
      throw error;
    }
  };

  const uploadEvidencia = async (file: File, cumplimientoId: string) => {
    if (!user) throw new Error('No user authenticated');

    if (!isOnline && !canStoreOffline(file)) {
      throw new Error(`Para subir archivos mayores a ${formatFileSize(MAX_OFFLINE_FILE_SIZE)}, conéctate a internet`);
    }

    try {
      const timestamp = Date.now();
      const tempId = `temp_${timestamp}`;

      if (!isOnline) {
        const fileData = await prepareFileForStorage(file);
        if (!fileData) {
          throw new Error('No se pudo preparar el archivo');
        }

        const tempEvidencia: EvidenciaCumplimiento = {
          id: tempId,
          cumplimiento_id: cumplimientoId,
          user_id: user.id,
          nombre_archivo: `${timestamp}-${file.name}`,
          nombre_original: file.name,
          tipo_archivo: file.type,
          tamano_archivo: file.size,
          url_archivo: 'pending',
          ruta_storage: 'pending',
          created_at: new Date().toISOString(),
        };

        await addPendingOperation('create', 'evidencias_cumplimiento', {
          ...tempEvidencia,
          fileData,
        });

        setEvidencias(prev => [tempEvidencia, ...prev]);

        toast({
          title: "Evidencia guardada offline",
          description: "Se subirá cuando te conectes a internet",
        });

        return;
      }

      const fileName = `${timestamp}-${file.name}`;
      const filePath = `${user.id}/${cumplimientoId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('evidencias-cumplimiento')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('evidencias-cumplimiento')
        .getPublicUrl(filePath);

      const { data: evidenciaData, error: dbError } = await supabase
        .from('evidencias_cumplimiento')
        .insert({
          cumplimiento_id: cumplimientoId,
          user_id: user.id,
          nombre_archivo: fileName,
          nombre_original: file.name,
          tipo_archivo: file.type,
          tamano_archivo: file.size,
          url_archivo: publicUrl,
          ruta_storage: filePath
        })
        .select()
        .single();

      if (dbError) throw dbError;

      setEvidencias(prev => [evidenciaData, ...prev]);
    } catch (error) {
      console.error('Error uploading evidencia:', error);
      throw error;
    }
  };

  const deleteEvidencia = async (evidenciaId: string) => {
    try {
      const evidencia = evidencias.find(e => e.id === evidenciaId);
      if (!evidencia) throw new Error('Evidencia not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('evidencias-cumplimiento')
        .remove([evidencia.ruta_storage]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('evidencias_cumplimiento')
        .delete()
        .eq('id', evidenciaId);

      if (dbError) throw dbError;

      // Remove from local state
      setEvidencias(prev => prev.filter(e => e.id !== evidenciaId));
    } catch (error) {
      console.error('Error deleting evidencia:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user && documentoId) {
      loadCumplimientos();
    }
  }, [user, documentoId]);

  // Subscribe to real-time changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('cumplimientos_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cumplimientos_documentos',
          filter: `documento_id=eq.${documentoId}`
        },
        () => {
          loadCumplimientos();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'evidencias_cumplimiento'
        },
        () => {
          if (cumplimientoActual) {
            loadEvidenciasForCumplimiento(cumplimientoActual.id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, documentoId, cumplimientoActual]);

  return {
    cumplimientos,
    cumplimientoActual,
    evidencias,
    loading,
    loadCumplimientos,
    selectCumplimiento,
    createNewCumplimiento,
    saveCumplimiento,
    deleteCumplimiento,
    uploadEvidencia,
    deleteEvidencia
  };
};